import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Phone,
  Mail,
  MessageSquare,
  Users,
  Award,
  StickyNote,
  FileText,
  ArrowLeftRight,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useRole } from '@/hooks/useRole';
import { CATEGORY_COLORS, CATEGORY_LABELS } from './types';
import { useDeleteClientEvent } from './useTimeline';
import type { TimelineCategory, TimelineItem } from './types';

const ICONS: Record<TimelineCategory, typeof Phone> = {
  contact_phone: Phone,
  contact_email: Mail,
  contact_whatsapp: MessageSquare,
  meeting: Users,
  milestone: Award,
  note: StickyNote,
  document_upload: FileText,
  transaction_created: ArrowLeftRight,
};

interface Props {
  item: TimelineItem;
}

export function TimelineItemRow({ item }: Props) {
  const Icon = ICONS[item.category];
  const colorClasses = CATEGORY_COLORS[item.category];
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useDeleteClientEvent();
  const { isStaff } = useRole();
  const canDelete = isStaff && !!item.manualEventId;

  return (
    <div className="relative flex gap-4">
      {/* Linha vertical + ícone */}
      <div className="flex flex-col items-center">
        <div
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 ${colorClasses}`}
        >
          <Icon size={16} />
        </div>
        <div className="w-px flex-1 bg-border" />
      </div>

      {/* Card de conteúdo */}
      <div className="flex-1 pb-6">
        <div className="rounded-lg border border-border bg-white p-3 transition-colors hover:bg-secondary/40">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${colorClasses}`}
                >
                  {CATEGORY_LABELS[item.category]}
                </span>
                <time
                  className="text-xs text-muted-foreground"
                  title={format(item.date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                >
                  {formatDistanceToNow(item.date, { locale: ptBR, addSuffix: true })}
                </time>
              </div>
              <h4 className="text-sm font-semibold text-foreground">{item.title}</h4>
              {item.description && (
                <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                  {item.description}
                </p>
              )}
              {item.authorName && (
                <p className="mt-1.5 text-xs text-muted-foreground/80">por {item.authorName}</p>
              )}
            </div>

            {canDelete && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setConfirmDelete(true)}
                disabled={remove.isPending}
                className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-expense"
                title="Excluir evento"
              >
                <Trash2 size={13} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {canDelete && item.manualEventId && (
        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir este evento?</AlertDialogTitle>
              <AlertDialogDescription>
                "{item.title}" será removido permanentemente da timeline.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => item.manualEventId && remove.mutate(item.manualEventId)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
